import { useMemo } from 'react';
import type { ClientMsg, Faction, GameEvent, GameSummary, Lang, RoleConfig, RosterEntry } from '../../../shared/src/types.js';
import { FACTION_LABEL } from '../../../shared/src/knowledge.js';
import { eventLine } from './gameOverEventLine.js';

interface Props {
  t: Lang;
  // 'draw' = simultaneous total wipe: no faction left alive, nobody wins.
  winner: { faction: Faction | 'draw'; roleIds?: string[] } | undefined;
  roleById: Record<string, RoleConfig>;
  myRole?: RoleConfig;
  summary?: GameSummary;
  // Whether the current viewer is the host. Drives which action buttons show.
  isHost: boolean;
  // Seat id of the current viewer (players only — host has no seat). Used to
  // tag the "you" row in the final roster. Undefined for the host.
  meId?: string;
  send: (msg: ClientMsg) => void;
}

export function GameOver({ t, winner, myRole, roleById, summary, isHost, meId, send }: Props) {
  // Pre-build the rain layer drops once per render — cheap (40 nodes) and
  // randomised left/duration so they look organic.
  const rainDrops = useMemo(
    () =>
      Array.from({ length: 40 }).map(() => ({
        left: Math.random() * 100,
        delay: -Math.random() * 2,
        dur: 1.2 + Math.random() * 1.6,
      })),
    [],
  );
  if (!winner) return null;
  const w = winner;
  // Solo wins are awarded by role, not by faction — multiple solos may be
  // alive at game-end and only the one named in roleIds (plus survivor
  // ride-alongs) actually won. Town/mafia wins remain faction-wide. The
  // predicate is generalized so it works for the viewer's own role AND for any
  // roster entry (per-row win/loss badge below).
  const didWin = (faction: Faction, roleId: string): boolean =>
    w.faction === 'draw'
      ? false // total wipe — nobody wins
      : w.faction === 'solo'
        ? !!w.roleIds?.includes(roleId)
        : faction === w.faction || !!w.roleIds?.includes(roleId);
  const won = !!myRole && didWin(myRole.faction, myRole.id);

  // One-line scoreboard for the headline: survivors per faction over the total
  // roster. Lets the host (who has no win/loss line) still read the outcome.
  const scoreboard = useMemo(() => {
    if (!summary) return null;
    const total = summary.finalRoster.length;
    const counts: Record<Faction, { alive: number; n: number }> = {
      town: { alive: 0, n: 0 },
      mafia: { alive: 0, n: 0 },
      solo: { alive: 0, n: 0 },
    };
    for (const r of summary.finalRoster) {
      counts[r.faction].n += 1;
      if (r.alive) counts[r.faction].alive += 1;
    }
    return { total, counts };
  }, [summary]);

  const nameById = useMemo(() => {
    const m = new Map<string, string>();
    if (summary) for (const r of summary.finalRoster) m.set(r.playerId, r.name);
    return m;
  }, [summary]);

  const grouped = useMemo(() => {
    if (!summary) return [];
    const groups = new Map<string, GameEvent[]>();
    for (const ev of summary.events) {
      const isDay = 'day' in ev;
      const num = isDay ? ev.day : (ev as any).night;
      const key = `${isDay ? 'D' : 'N'}-${num}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(ev);
    }
    return [...groups.entries()].map(([key, events]) => {
      const isDay = key.startsWith('D');
      const num = Number(key.slice(2));
      return { key, isDay, num, events };
    });
  }, [summary]);

  return (
    <div className="scr">
      <div className="bg-noir" />
      <div className="vig vignette-pulse" />

      <div className="go-scroll">
      <div className="go-stage">
        <div className="go-rays" />
        <div className={`go-bloom ${winner.faction}`} />
        <div className={`go-rain ${winner.faction}`}>
          {rainDrops.map((d, i) => (
            <i
              key={i}
              style={{
                left: `${d.left}%`,
                ['--del' as never]: `${d.delay}s`,
                ['--t' as never]: `${d.dur}s`,
              }}
            />
          ))}
        </div>
        <div className="display go-title go-title-anim">
          {won
            ? t === 'ru' ? 'ПОБЕДА' : 'ПЕРЕМОГА'
            : w.faction === 'draw'
              ? t === 'ru' ? 'НИЧЬЯ' : 'НІЧИЯ'
              : myRole
                ? t === 'ru' ? 'ПРОИГРЫШ' : 'ПОРАЗКА'
                : t === 'ru' ? 'ИГРА ОКОНЧЕНА' : 'ГРУ ЗАВЕРШЕНО'}
        </div>
        <div className={`display go-faction ${winner.faction}`}>
          {winner.faction === 'draw'
            ? (t === 'ru' ? 'НИЧЬЯ' : 'НІЧИЯ')
            : FACTION_LABEL[winner.faction][t]}
        </div>
        {scoreboard && (
          <div className="go-scoreboard mono">
            {(() => {
              const c = scoreboard.counts;
              const aliveTotal = c.town.alive + c.mafia.alive + c.solo.alive;
              const parts: string[] = [];
              // Survivors per faction; skip factions with nobody in the game.
              if (c.town.n) parts.push(`${FACTION_LABEL.town[t]} ${c.town.alive}/${c.town.n}`);
              if (c.mafia.n) parts.push(`${FACTION_LABEL.mafia[t]} ${c.mafia.alive}/${c.mafia.n}`);
              if (c.solo.n) parts.push(`${FACTION_LABEL.solo[t]} ${c.solo.alive}/${c.solo.n}`);
              const survived = t === 'ru'
                ? `ВЫЖИЛИ ${aliveTotal}/${scoreboard.total}`
                : `ВИЖИЛИ ${aliveTotal}/${scoreboard.total}`;
              return `${survived} · ${parts.join(' · ')}`;
            })()}
          </div>
        )}
        <div className="go-actions">
          {isHost ? (
            <>
              <button
                className="btn btn-blood"
                onClick={() => send({ kind: 'host:reset-to-lobby' })}
              >
                {t === 'ru' ? 'ИГРАТЬ СНОВА' : 'ГРАТИ ЩЕ'}
              </button>
              {/* Disband is destructive — push it into a separate, quieter tier
                  below the primary play-again so it isn't one tap apart. */}
              <div
                className="go-actions-secondary"
                style={{ marginTop: 18, display: 'flex', justifyContent: 'center' }}
              >
                <button
                  className="btn btn-ghost go-disband"
                  style={{ fontSize: '0.78rem', opacity: 0.72 }}
                  onClick={() => {
                    const msg = t === 'ru'
                      ? 'Распустить комнату? Все игроки будут отключены.'
                      : 'Розпустити кімнату? Усі гравці будуть відключені.';
                    if (window.confirm(msg)) send({ kind: 'host:disband' });
                  }}
                >
                  {t === 'ru' ? 'РАСПУСТИТЬ' : 'РОЗПУСТИТИ'}
                </button>
              </div>
            </>
          ) : (
            <button
              className="btn btn-ghost"
              onClick={() => send({ kind: 'player:leave' })}
            >
              {t === 'ru' ? 'ВЫЙТИ' : 'ВИЙТИ'}
            </button>
          )}
        </div>
      </div>

      {summary && (
        <div className="go-summary">
          <section className="go-roster">
            <h2 className="go-section-title">{t === 'ru' ? 'РОЛИ' : 'РОЛІ'}</h2>
            <ul className="go-roster-grid">
              {summary.finalRoster.map((r: RosterEntry) => {
                const role = roleById[r.roleId];
                const isMe = !!meId && r.playerId === meId;
                const rowWon = didWin(r.faction, r.roleId);
                return (
                  <li
                    key={r.playerId}
                    className={`go-roster-item faction-${r.faction} ${r.alive ? '' : 'dead'} ${isMe ? 'is-me' : ''} ${rowWon ? 'is-win' : 'is-loss'}`}
                    style={{ ['--accent' as never]: role?.accent ?? '#fff' }}
                  >
                    <span className="go-roster-name">
                      <span className="go-roster-name-text">{r.name}</span>
                      {isMe && (
                        <span className="go-roster-you">{t === 'ru' ? 'ВЫ' : 'ВИ'}</span>
                      )}
                    </span>
                    <span className="go-roster-role">{role?.title[t] ?? r.roleId}</span>
                    <span
                      className={`go-roster-result ${rowWon ? 'win' : 'loss'}`}
                      style={{ color: rowWon ? 'var(--toxic-green, #9be15d)' : 'var(--poison-red, #c4332f)' }}
                    >
                      {rowWon
                        ? (t === 'ru' ? 'ПОБЕДА' : 'ПЕРЕМОГА')
                        : (t === 'ru' ? 'ПРОИГРЫШ' : 'ПОРАЗКА')}
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
          <section className="go-timeline">
            <h2 className="go-section-title">{t === 'ru' ? 'ХРОНИКА' : 'ХРОНІКА'}</h2>
            {grouped.map((g) => (
              <div key={g.key} className="go-timeline-group">
                <div className="go-timeline-header">
                  {g.isDay
                    ? (t === 'ru' ? `День ${g.num}` : `День ${g.num}`)
                    : (t === 'ru' ? `Ночь ${g.num}` : `Ніч ${g.num}`)}
                </div>
                <ul className="go-timeline-list">
                  {g.events.map((ev, i) => {
                    const line = eventLine(ev, { t, nameById, roleById });
                    return (
                      <li
                        key={i}
                        className={`go-timeline-line tone-${line.tone}${line.weight === 'major' ? ' is-major' : ''}`}
                        style={line.weight === 'major' ? { fontWeight: 700 } : undefined}
                      >
                        {line.text}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </section>
        </div>
      )}
      </div>
    </div>
  );
}
